import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, HAITI_DEPARTMENTS, productImageUrl } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useReferral } from "../context/ReferralContext";
import { CreditCard, MapPin, BadgePercent, Smartphone, Banknote, Truck, Upload, CheckCircle2, DollarSign, Building2 } from "lucide-react";
import { toast } from "sonner";
import Spinner from "../components/Spinner";

const PAYMENT_METHODS = [
  {
    id: "moncash",
    label: "MonCash",
    icon: Smartphone,
    color: "#E3000F",
    receiver: "Djonny Floreal",
    number: "+509 4840 2525",
  },
  {
    id: "natcash",
    label: "NatCash",
    icon: Banknote,
    color: "#0052CC",
    receiver: "Malicia Danaicka Metayer",
    number: "+509 3219 0716",
  },
  {
    id: "zelle",
    label: "Zelle",
    icon: DollarSign,
    color: "#6D1ED4",
    receiver: "Love Sony Saint-Luc",
    number: "+1 774 719-3244",
    note: "Envoyez le montant en USD équivalent via Zelle à ce numéro.",
  },
  {
    id: "bank_transfer",
    label: "Virement bancaire",
    icon: Building2,
    color: "#0F766E",
    receiver: "Bientôt disponible",
    number: "—",
    note: "Nos coordonnées bancaires seront communiquées prochainement. Contactez-nous sur WhatsApp pour un paiement bancaire.",
    disabled: true,
  },
  {
    id: "cash_on_delivery",
    label: "Paiement à la livraison",
    icon: Truck,
    color: "#16A34A",
  },
];

export default function CheckoutPage() {
  const { items, totals, clear } = useCart();
  const { user, loading: authLoading } = useAuth();
  const { format: formatGdes } = useCurrency();
  const { code: refCode, ambassador } = useReferral();
  const nav = useNavigate();

  // Enforce authentication for placing an order
  useEffect(() => {
    if (!authLoading && !user) {
      toast.info("Vous devez être connecté pour finaliser une commande.");
      nav("/login?redirect=/checkout", { replace: true });
    }
  }, [authLoading, user, nav]);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: "",
    address: "",
    city: "",
    department: "Ouest",
    notes: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("moncash");
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryDays, setDeliveryDays] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  // Payment proof (MonCash / NatCash) — submitted with the order
  const [payProof, setPayProof] = useState({ sender_name: "", sender_phone: "", reference: "", file: null });
  const proofFileRef = useRef(null);
  const [htgPerUsd, setHtgPerUsd] = useState(132);

  useEffect(() => {
    api.get("/exchange-rate").then(({ data }) => setHtgPerUsd(Number(data.htg_per_usd) || 132)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Compute delivery fee whenever department/city changes
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/delivery-fees/calculate", {
          params: { department: form.department, city: form.city || undefined },
        });
        if (!mounted) return;
        setDeliveryFee(data.fee || 0);
        setDeliveryDays(data.estimated_days || "");
      } catch {
        setDeliveryFee(0);
        setDeliveryDays("");
      }
    };
    load();
    return () => { mounted = false; };
  }, [form.department, form.city]);

  const promoDiscount = promoApplied ? totals.subtotal * (promoApplied.discount_percent / 100) : 0;
  const referralDiscount = ambassador ? totals.subtotal * ((ambassador.discount_percent || 0) / 100) : 0;
  const finalTotal = totals.subtotal - promoDiscount - referralDiscount + deliveryFee;

  const applyPromo = async () => {
    setPromoError("");
    if (!promoCode.trim()) return;
    try {
      const { data } = await api.post("/promo-codes/validate", { code: promoCode });
      setPromoApplied(data);
      toast.success(`Code ${data.code} appliqué (-${data.discount_percent}%)`);
    } catch (err) {
      setPromoApplied(null);
      setPromoError(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Panier vide"); return; }
    // Validate payment proof fields when a mobile/electronic payment method is chosen
    const PROOF_METHODS = new Set(["moncash", "natcash", "zelle", "bank_transfer"]);
    if (PROOF_METHODS.has(paymentMethod)) {
      if (paymentMethod === "bank_transfer") {
        toast.info("Virement bancaire indisponible pour l'instant. Contactez-nous sur WhatsApp.");
        return;
      }
      if (!payProof.sender_name.trim() || !payProof.sender_phone.trim() || !payProof.reference.trim()) {
        toast.error("Merci de renseigner le nom du payeur, le téléphone et l'ID de transaction");
        return;
      }
      if (!payProof.file) {
        toast.error("Merci d'ajouter une capture d'écran comme preuve de paiement");
        return;
      }
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, color_breakdown: i.color_breakdown || null, technician_qty: Number(i.technician_qty) || 0, technician_price: Number(i.technician_price) || 0, selected_options: i.selected_options || null })),
        shipping_address: form,
        payment_method: paymentMethod,
        promo_code: promoApplied?.code || null,
        delivery_fee: deliveryFee,
        ambassador_code: refCode || null,
      });
      // If MonCash / NatCash / Zelle — immediately upload the proof so status becomes awaiting_verification
      if (["moncash", "natcash", "zelle"].includes(paymentMethod) && payProof.file) {
        try {
          const fd = new FormData();
          fd.append("file", payProof.file);
          const qs = new URLSearchParams({
            sender_name: payProof.sender_name.trim(),
            sender_phone: payProof.sender_phone.trim(),
            reference: payProof.reference.trim(),
          });
          await api.post(`/orders/track/${data.tracking_number}/payment-proof?${qs.toString()}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error("Commande créée, mais l'envoi de la preuve a échoué : " + formatApiError(err) + ". Vous pouvez la renvoyer depuis la page de suivi.");
        }
      }
      clear();
      toast.success("Commande créée avec succès !");
      // MonCash auto-gateway: if user chose MonCash, redirect to hosted payment page
      if (paymentMethod === "moncash") {
        try {
          const { data: mc } = await api.post("/payments/moncash/create", { order_id: data.order_id });
          if (mc?.checkout_url) {
            // Persist a note to return to tracking after
            window.location.href = mc.checkout_url;
            return;
          }
        } catch (err) {
          toast.error("Impossible d'ouvrir MonCash : " + formatApiError(err) + ". Vous pouvez terminer le paiement depuis votre suivi.");
        }
      }
      nav(`/track/${data.tracking_number}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return <div className="je-container py-20 text-center"><p className="text-gray-500">Votre panier est vide.</p></div>;
  }

  const selected = PAYMENT_METHODS.find((p) => p.id === paymentMethod);

  return (
    <div className="je-container py-10" data-testid="checkout-page">
      <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Commander</h1>
      <div className="rounded-md bg-blue-50 border border-blue-100 text-[#003D99] p-3 mb-6 inline-flex items-center gap-2" data-testid="checkout-promo-banner">
        <BadgePercent className="w-4 h-4" />
        <span className="text-sm font-semibold">Les promotions sont appliquées directement sur les produits concernés.</span>
      </div>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-6">
          <section className="bg-white border rounded-xl p-5">
            <h3 className="font-bold flex items-center gap-2 mb-4" style={{ fontFamily: "Outfit" }}>
              <MapPin className="w-5 h-5 text-[#0052CC]" /> Adresse de livraison
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <input required value={form.full_name} onChange={update("full_name")} placeholder="Nom complet" className="border rounded-md px-3 py-2 text-sm" data-testid="ship-name" />
              <input required value={form.phone} onChange={update("phone")} placeholder="Téléphone (+509…)" className="border rounded-md px-3 py-2 text-sm" data-testid="ship-phone" />
              <input required value={form.address} onChange={update("address")} placeholder="Adresse" className="md:col-span-2 border rounded-md px-3 py-2 text-sm" data-testid="ship-address" />
              <input required value={form.city} onChange={update("city")} placeholder="Ville (ex: Pétion-Ville)" className="border rounded-md px-3 py-2 text-sm" data-testid="ship-city" />
              <select value={form.department} onChange={update("department")} className="border rounded-md px-3 py-2 text-sm bg-white" data-testid="ship-department">
                {HAITI_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <textarea value={form.notes} onChange={update("notes")} placeholder="Instructions particulières (optionnel)" rows={2} className="md:col-span-2 border rounded-md px-3 py-2 text-sm" />
            </div>
          </section>

          <section className="bg-white border rounded-xl p-5">
            <h3 className="font-bold flex items-center gap-2 mb-4" style={{ fontFamily: "Outfit" }}>
              <CreditCard className="w-5 h-5 text-[#0052CC]" /> Méthode de paiement
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => !m.disabled && setPaymentMethod(m.id)}
                  className={`border-2 rounded-xl p-3 text-left transition relative ${paymentMethod === m.id ? "border-[#0052CC] bg-blue-50" : "border-gray-200 hover:border-gray-300"} ${m.disabled ? "opacity-70" : ""}`}
                  data-testid={`pm-${m.id}`}
                >
                  <m.icon className="w-6 h-6 mb-1" style={{ color: m.color }} />
                  <p className="font-bold text-sm">{m.label}</p>
                  {m.disabled && <span className="absolute top-1 right-1 bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded">Bientôt</span>}
                </button>
              ))}
            </div>

            {selected && (selected.id === "moncash" || selected.id === "natcash" || selected.id === "zelle") && (
              <>
                <div className="mt-4 border-l-4 rounded p-4 bg-gray-50" style={{ borderColor: selected.color }} data-testid="payment-instructions">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">📋 Instructions {selected.label}</p>
                  <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700 mb-3">
                    <li>Ouvrez votre application <b>{selected.label}</b>.</li>
                    <li>
                      Envoyez <b>{formatGdes(finalTotal)}</b>
                      {selected.id === "zelle" && (
                        <> soit <b className="text-[#6D1ED4]">≈ ${(finalTotal / htgPerUsd).toFixed(2)} USD</b> <span className="text-xs text-gray-500">(taux : 1 USD = {htgPerUsd} HTG)</span></>
                      )} à :
                    </li>
                  </ol>
                  <div className="pl-5 mb-3">
                    <p className="font-bold text-base" style={{ color: selected.color }}>{selected.receiver}</p>
                    <p className="font-mono text-lg font-bold">{selected.number}</p>
                    {selected.note && <p className="text-xs text-gray-500 mt-1 italic">{selected.note}</p>}
                  </div>
                  <ol start="3" className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                    <li>Notez l&apos;<b>ID de transaction</b> reçu par SMS ou email.</li>
                    <li>Faites une <b>capture d&apos;écran</b> du reçu de paiement.</li>
                    <li>Remplissez le formulaire ci-dessous pour joindre votre preuve à la commande.</li>
                  </ol>
                </div>

                {/* Payment proof form — always shown alongside instructions */}
                <div className="mt-4 border-2 rounded-xl p-5" style={{ borderColor: `${selected.color}55` }} data-testid="payment-proof-form">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4" style={{ color: selected.color }} />
                    <h4 className="text-sm font-bold uppercase tracking-widest" style={{ color: selected.color }}>Preuve de paiement {selected.label}</h4>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du payeur *</label>
                      <input
                        type="text"
                        value={payProof.sender_name}
                        onChange={(e) => setPayProof({ ...payProof, sender_name: e.target.value })}
                        placeholder="Nom complet sur le compte"
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid="checkout-proof-sender-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {selected.id === "zelle" ? "Téléphone ou email Zelle *" : "Téléphone utilisé *"}
                      </label>
                      <input
                        type="tel"
                        value={payProof.sender_phone}
                        onChange={(e) => setPayProof({ ...payProof, sender_phone: e.target.value })}
                        placeholder={selected.id === "zelle" ? "+1 XXX XXX-XXXX" : "+509 XXXX XXXX"}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid="checkout-proof-sender-phone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">ID / N° de référence de la transaction *</label>
                      <input
                        type="text"
                        value={payProof.reference}
                        onChange={(e) => setPayProof({ ...payProof, reference: e.target.value })}
                        placeholder="Ex: TX123456789"
                        className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                        data-testid="checkout-proof-reference"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Capture d&apos;écran du paiement *</label>
                      <input
                        ref={proofFileRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setPayProof({ ...payProof, file: e.target.files?.[0] || null })}
                        className="hidden"
                        data-testid="checkout-proof-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => proofFileRef.current?.click()}
                        className="w-full border-2 border-dashed border-gray-300 rounded-md p-3 text-sm inline-flex items-center justify-center gap-2 text-gray-600 hover:border-[#0052CC] hover:text-[#0052CC] transition"
                        data-testid="checkout-proof-file-btn"
                      >
                        <Upload className="w-4 h-4" />
                        {payProof.file ? payProof.file.name : "Choisir un fichier (JPG, PNG, PDF, max 8 Mo)"}
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-[11px] text-gray-500">
                    🔒 Votre paiement sera vérifié manuellement par l&apos;administrateur. Vous recevrez un email dès qu&apos;il sera confirmé.
                  </p>
                </div>
              </>
            )}
            {selected?.id === "bank_transfer" && (
              <div className="mt-4 border-l-4 border-teal-600 rounded p-4 bg-teal-50" data-testid="payment-bank-notice">
                <p className="text-xs font-semibold uppercase tracking-widest text-teal-800 mb-2">🏦 Virement bancaire</p>
                <p className="text-sm text-gray-700 mb-2">
                  Nos coordonnées bancaires (IBAN / SWIFT / numéro de compte) seront disponibles très prochainement.
                </p>
                <p className="text-sm text-gray-700">
                  En attendant, contactez-nous sur WhatsApp au <b>+509 3219 0716</b> pour finaliser votre paiement par virement bancaire. Nous mettrons à jour cette option dès que possible.
                </p>
              </div>
            )}
            {selected?.id === "cash_on_delivery" && (
              <p className="mt-3 text-sm text-gray-600" data-testid="payment-instructions-cod">
                Vous paierez en espèces au livreur au moment de la remise du colis.
              </p>
            )}
          </section>
        </div>

        <aside className="bg-white border rounded-xl p-5 h-fit sticky top-44">
          <h3 className="font-bold mb-4" style={{ fontFamily: "Outfit" }}>Votre commande</h3>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3 items-center">
                <div className="w-12 h-12 bg-[#F3F4F6] rounded flex items-center justify-center overflow-hidden">
                  <img src={productImageUrl(i.image)} alt="" className="max-h-full max-w-full object-contain p-1" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold line-clamp-1">{i.name}</p>
                  <p className="text-xs text-gray-500">x{i.quantity} • {formatGdes(i.price)}</p>
                </div>
                <p className="text-sm font-semibold">{formatGdes(i.price * i.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="border-t mt-4 pt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Sous-total</span><span className="font-semibold">{formatGdes(totals.subtotal)}</span></div>
            <div className="pt-2 pb-1">
              <div className="flex gap-1.5">
                <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="Code promo" className="flex-1 border rounded px-2 py-1.5 text-sm uppercase" data-testid="promo-input" />
                <button type="button" onClick={applyPromo} className="bg-[#0052CC] text-white text-xs font-semibold px-3 rounded hover:bg-[#003D99]" data-testid="promo-apply">Appliquer</button>
              </div>
              {promoError && <p className="text-xs text-red-600 mt-1">{promoError}</p>}
            </div>
            {promoApplied && (
              <div className="flex justify-between text-[#16A34A]"><span>Code {promoApplied.code}</span><span className="font-semibold">−{formatGdes(promoDiscount)}</span></div>
            )}
            {ambassador && (
              <div className="flex justify-between text-[#16A34A]" data-testid="ambassador-discount-row">
                <span>Ambassadeur <b>{ambassador.code}</b> (−{ambassador.discount_percent}%)</span>
                <span className="font-semibold">−{formatGdes(referralDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-gray-700">
              <span>Frais de livraison {deliveryDays && <span className="text-xs text-gray-400">({deliveryDays})</span>}</span>
              <span className="font-semibold">{deliveryFee > 0 ? formatGdes(deliveryFee) : "Gratuit"}</span>
            </div>
            <div className="flex justify-between text-base pt-2 border-t mt-2"><span className="font-bold">Total</span><span className="font-extrabold" style={{ fontFamily: "Outfit" }}>{formatGdes(finalTotal)}</span></div>
          </div>
          <button type="submit" disabled={loading} className="je-btn-primary w-full mt-5 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="place-order-btn">
            {loading && <Spinner className="w-4 h-4" />}
            {loading ? "Traitement…" : "Confirmer la commande"}
          </button>
        </aside>
      </form>
    </div>
  );
}
